import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api.js";
import type { Id } from "../../convex/_generated/dataModel.js";

export type PipelineStage = "new" | "contacted" | "qualified" | "interested" | "committed" | "participant" | "closed_lost";

export interface Prospect {
  _id: Id<"prospects">;
  company: string;
  contactName: string;
  email: string;
  phone?: string;
  stage: PipelineStage;
  notes?: string;
  createdAt: number;
  updatedAt: number;
  userId: Id<"users">;
}

export interface CreateProspectData {
  company: string;
  contactName: string;
  email: string;
  phone?: string;
  title?: string;
  stage?: PipelineStage;
  notes?: string;
  isPrimaryContact?: boolean;
}

export interface UpdateProspectData {
  company?: string;
  contactName?: string;
  email?: string;
  phone?: string;
  stage?: PipelineStage;
  notes?: string;
}

export function useProspects() {
  const prospects = useQuery(api.prospects.getProspects);
  const pipelineStats = useQuery(api.prospects.getPipelineStats);
  
  const createProspect = useMutation(api.prospects.createProspect);
  const createProspectWithFirm = useMutation(api.prospects.createProspectWithFirm);
  const updateProspect = useMutation(api.prospects.updateProspect);
  const updateProspectStage = useMutation(api.prospects.updateProspectStage);
  const deleteProspect = useMutation(api.prospects.deleteProspect);
  const bulkCreateProspects = useMutation(api.prospects.bulkCreateProspects);
  

  const getProspectsByStage = (stage: PipelineStage) => {
    return prospects?.filter((p: Prospect) => p.stage === stage) || [];
  };

  const moveProspectToStage = async (prospectId: Id<"prospects">, newStage: PipelineStage) => {
    await updateProspectStage({ id: prospectId, stage: newStage });
  };

  const addProspect = async (data: CreateProspectData) => {
    // Use the new compound function that handles firm creation
    return await createProspectWithFirm({
      contactName: data.contactName,
      email: data.email,
      phone: data.phone,
      title: data.title,
      company: data.company,
      stage: data.stage,
      notes: data.notes,
      isPrimaryContact: data.isPrimaryContact
    });
  };

  const editProspect = async (id: Id<"prospects">, data: UpdateProspectData) => {
    return await updateProspect({ id, ...data });
  };

  const removeProspect = async (id: Id<"prospects">) => {
    return await deleteProspect({ id });
  };

  const importProspects = async (prospectsData: CreateProspectData[]) => {
    return await bulkCreateProspects({ prospects: prospectsData });
  };

  return {
    prospects: prospects || [],
    pipelineStats,
    getProspectsByStage,
    moveProspectToStage,
    addProspect,
    editProspect,
    removeProspect,
    importProspects,
    isLoading: prospects === undefined,
  };
}